import { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  DialogActions,
  DialogContent,
} from "@mui/material";
import ResponsiveDialog from "../UI/ResponsiveDialog";
import { useMutation } from "@apollo/client";
import {
  ADD_APPEARANCE,
  GET_APPEARANCES_BY_MATCH_ID,
  APPEARANCES_WITH_PERMISSION,
} from "../../GraphQL/Appearances";
import useNotification from "../../hooks/useNotification.js";
import PlayerSelect from "../Player/PlayerSelect";

const NewAppearanceDialog = (props) => {
  const [appearance, setAppearance] = useState("");
  const [player, setPlayer] = useState(null);
  const { notifySuccess } = useNotification();

  const isValid = player !== null;

  const [addAppearance, { loading, error }] = useMutation(ADD_APPEARANCE, {
    variables: {
      matchId: props.matchId,
      teamId: props.teamId,
      playerId: player,
      goals: parseInt(appearance.goals),
      assists: parseInt(appearance.assists),
      passes: parseInt(appearance.passes),
      interceptions: parseInt(appearance.interceptions),
      tackles: parseInt(appearance.tackles),
      yellowCards: parseInt(appearance.yellowCards),
      redCards: parseInt(appearance.redCards),
    },
    refetchQueries: [GET_APPEARANCES_BY_MATCH_ID, APPEARANCES_WITH_PERMISSION],
    onCompleted: () => { notifySuccess("Appearance created."); props.setOpen(false); },
  });

  return (
    <div>
      <ResponsiveDialog
        open={props.open}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isValid) {
            addAppearance();
          } else if (e.key === "Escape") {
            props.setOpen(false);
          }
        }}
        title="Appearance"
        onClose={() => props.setOpen(false)}
      >
        <DialogContent>
          <PlayerSelect
            player={player}
            setPlayer={setPlayer}
            players={props.players}
          />
          <TextField
            data-testid="goals"
            autoFocus
            margin="dense"
            id="goals"
            label="Goals"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 50) value = 50;
              if (value < 0) value = 0;
              e.target.value = value;
              setAppearance({ ...appearance, goals: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 50 } }}
          />
          <TextField
            data-testid="assists"
            margin="dense"
            id="assists"
            label="Assists"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 50) value = 50;
              if (value < 0) value = 0;
              e.target.value = value;
              setAppearance({ ...appearance, assists: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 50 } }}
          />
          <TextField
            data-testid="passes"
            margin="dense"
            id="passes"
            label="Passes"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 50) value = 50;
              if (value < 0) value = 0;
              e.target.value = value;
              setAppearance({ ...appearance, passes: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 50 } }}
          />
          <TextField
            data-testid="interceptions"
            margin="dense"
            id="interceptions"
            label="Interceptions"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 50) value = 50;
              if (value < 0) value = 0;
              e.target.value = value;
              setAppearance({ ...appearance, interceptions: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 50 } }}
          />
          <TextField
            data-testid="tackles"
            margin="dense"
            id="tackles"
            label="Tackles"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 50) value = 50;
              if (value < 0) value = 0;
              e.target.value = value;
              setAppearance({ ...appearance, tackles: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 50 } }}
          />
          <TextField
            data-testid="yellowCards"
            margin="dense"
            id="yellowCards"
            label="Yellow Cards"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 2) value = 2;
              if (value < 0) value = 0;
              e.target.value = value;
              setAppearance({ ...appearance, yellowCards: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 2 } }}
          />
          <TextField
            data-testid="redCards"
            margin="dense"
            id="redCards"
            label="Red Cards"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value > 1) value = 1;
              if (value < 0) value = 0;
              e.target.value = value;
              setAppearance({ ...appearance, redCards: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 1 } }}
          />
        </DialogContent>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error.message}
          </Alert>
        )}
        <DialogActions>
          <Button
            data-testid="saveNewAppearanceDialogButton"
            variant="contained"
            onClick={() => addAppearance()}
            disabled={loading || !isValid}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              "Create"
            )}
          </Button>
          <Button
            data-testid="closeNewAppearanceDialogButton"
            onClick={() => props.setOpen(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </div>
  );
};

export default NewAppearanceDialog;
